import { Box, Grid, Paper, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AgentLocation, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import AgentPersonnelSummariesContext from "../../contexts/AgentPersonnelContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAgentPersonnelSummaries from "../../hooks/data/useAgentPersonnelSummaries";

const useAgentLocationColumns = () => {

    const agentColumns = useMemo(() => {
        const columns: GridColDef<AgentLocation>[] =  [
            {
                field: "agentName",
                headerName: "Agent",
                flex: 1
            },
            {
                field: "location",
                headerName: "Location",
                flex: 1
            },
            {
                field: "personnel",
                headerName: "Personnel",
                flex: 1
            },
            {
                field: "actions",
                headerName: '',
                renderCell: (params) => {
                    return (
                        <GridActions
                            editPermission={Permissions.AdminAccess}
                            editUrl={`/admin/agent-personnel/edit/${params.row.id}`}
                        />
                    );
                },
                disableExport: true
            }
        ];

        return columns;
    }, []);

    return agentColumns;
}


const Agents:FC = () => {

    const columns = useAgentLocationColumns();

    const theme = useTheme();
    
    const context = useAgentPersonnelSummaries();

    const {data: agentPersonnel, loading: agentPersonnelLoading} = context;

    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Agent Personnel`,
        exportTitle: `Agent Personnel`,
        includeDateTime: true
    }), []);

    return (
        <AgentPersonnelSummariesContext.Provider value={context}>
            <SetLinks
                createProjectLink={DefaultProjectCreateLink}
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <Outlet />
            <Grid
                container
                gap={theme.spacing(2)}
                justifyContent={"end"}
                marginBottom={theme.spacing(2)}
            >
                <StyledButton 
                        variant="contained"
                        //@ts-expect-error
                        component={Link}
                        to={`/admin/agent-personnel/create`}
                        state={{
                            back: true
                        }}
                >
                    Add Agent Personnel
                </StyledButton>
            </Grid>
            <Paper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                >
                    <CollapsibleTablePremium
                        density="compact"
                        startCollapsed={false} 
                        title={"Agent Personnel"}
                        pluralizeTitle={false}
                        pluralizeEntity={false} 
                        loading={agentPersonnelLoading} 
                        columns={columns}
                        rows={agentPersonnel}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </Paper>
        </AgentPersonnelSummariesContext.Provider>
    );
}

export default Agents;